import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { Drawer } from '../../components/Drawer';
import { Field, inputClass } from '../../components/Field';
import { FormProblem } from '../../components/FormProblem';
import { useToast } from '../../components/Toast';
import { timeZones } from '../../lib/time';
import { useAuth } from '../auth/AuthProvider';
import { createTenant, noProblem, suggestSlug, tenantsKey, toProblem, type Problem } from './api';

interface Props {
  onClose: () => void;
}

/** Creates a tenant with its first administrator (ADR-028). */
export function TenantDrawer({ onClose }: Props) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const zones = useMemo(() => timeZones(), []);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  // The identifier follows the name until the operator edits it.
  const [slugEdited, setSlugEdited] = useState(false);
  const [timezone, setTimezone] = useState('America/Sao_Paulo');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [problem, setProblem] = useState<Problem>(noProblem);

  const save = useMutation({
    mutationFn: () =>
      createTenant(request, {
        slug,
        name,
        timezone,
        admin_email: adminEmail,
        admin_name: adminName,
        admin_password: adminPassword,
      }),
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: tenantsKey });
      notify(
        `Empresa ${t.name} criada. O administrador já pode entrar com o identificador "${t.slug}".`,
      );
      onClose();
    },
    onError: (err) => {
      setProblem(toProblem(err));
    },
  });

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    setProblem(noProblem);
    save.mutate();
  };

  return (
    <Drawer
      title="Nova empresa"
      description="A empresa nasce com os perfis padrão e com o primeiro administrador geral, que cadastra o restante."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" form="tenant-form" disabled={save.isPending}>
            {save.isPending ? 'Criando…' : 'Criar empresa'}
          </Button>
        </>
      }
    >
      <form id="tenant-form" onSubmit={submit} noValidate className="space-y-4">
        <FormProblem message={problem.form} />
        <fieldset className="space-y-4">
          <legend className="mb-1 text-2xs font-medium tracking-wide text-fg-subtle uppercase">
            Empresa
          </legend>
          <Field label="Nome" error={problem.fields.name}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                data-autofocus
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugEdited) setSlug(suggestSlug(e.target.value));
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={inputClass}
              />
            )}
          </Field>
          <Field
            label="Identificador"
            hint="Digitado no login das pessoas da empresa. Não pode ser alterado depois."
            error={problem.fields.slug}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                value={slug}
                autoCapitalize="off"
                spellCheck={false}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugEdited(true);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={`${inputClass} font-mono`}
              />
            )}
          </Field>
          <Field label="Fuso horário" error={problem.fields.timezone}>
            {({ id, describedBy, invalid }) => (
              <select
                id={id}
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={inputClass}
              >
                {zones.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </fieldset>

        <div aria-hidden="true" className="border-t border-line" />
        <fieldset className="space-y-4">
          <legend className="mb-1 text-2xs font-medium tracking-wide text-fg-subtle uppercase">
            Primeiro administrador
          </legend>
          <Field label="Nome" error={problem.fields.admin_name}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                value={adminName}
                onChange={(e) => {
                  setAdminName(e.target.value);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={inputClass}
              />
            )}
          </Field>
          <Field label="E-mail" error={problem.fields.admin_email}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                type="email"
                autoComplete="off"
                value={adminEmail}
                onChange={(e) => {
                  setAdminEmail(e.target.value);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={inputClass}
              />
            )}
          </Field>
          <Field
            label="Senha inicial"
            hint="Pelo menos 12 caracteres. Passe-a ao administrador por um canal seguro; ele pode trocá-la depois."
            error={problem.fields.admin_password}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                type="password"
                autoComplete="new-password"
                value={adminPassword}
                onChange={(e) => {
                  setAdminPassword(e.target.value);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={inputClass}
              />
            )}
          </Field>
        </fieldset>
      </form>
    </Drawer>
  );
}
